// TODO refactor
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Signal,
  signal,
  WritableSignal, OnDestroy
} from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import {
  faExclamationCircle,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faPaperPlane
} from '@fortawesome/pro-light-svg-icons';
import { ActivatedRoute, Router } from "@angular/router";
import { passwordMatchValidator, passwordValidator } from "../../../../../shared/common/validators.common";
import { AuthService } from "@features/authenticate/services/auth.service";
import { CapsLockNoticeComponent } from "../../../../../shared/components/caps-lock-notice/caps-lock-notice.component";
import { HeadlineDescriptionComponent } from "../../ui/headline/components/description/headline-description.component";
import { HeadlineComponent } from "../../ui/headline/headline.component";
import { HeadlineTitleComponent } from "../../ui/headline/components/title/headline-title.component";
import { MatInputModule } from "@angular/material/input";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { AUTHENTICATE_PARAM_MAPS, AUTHENTICATE_ROUTES } from '@features/authenticate/constants/routes.constant';
import { NotifyChipComponent } from "@shared/components/notify-chip/notify-chip.component";
import { NotifyChipValueComponent } from "@shared/components/notify-chip/components/notify-chip-value/notify-chip-value.components";
import { CORE_ROUTES } from "@core/constants/routes.constant";
import { SET_PASSWORD_TOKENS } from "@features/authenticate/types/set-password-tokens";
import { REGISTER_USER_TOKENS } from "@features/authenticate/types/register-user-tokens";
import { Observable } from "rxjs";
import { RegisterUserResponse } from "@features/authenticate/types/register-user-response";
import { provideTranslationScope, TranslationPipeModule } from "@ogs-gmbh/ngx-translate";
import { TranslationInfo } from "@shared/types/translation-info";
import { KeycloakService } from "@ogs/ngx-keycloak";
import { toSignal } from "@angular/core/rxjs-interop";
import { LanguageSelectionService } from '@shared/components/language-selection/services/language-selection/language-selection.service';
import { TemplatePipeModule } from '@ogs-gmbh/ngx-template-engine';

interface ServerErrorMessage {
  localizedMessage?: TranslationInfo | undefined;
  message?: TranslationInfo | undefined;
}

const TRANSLATION_SCOPE: string = "feature/auth/set-password";
const RESEND_LINK_COOLDOWN_SECONDS: number = 120;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-authenticate-page-set-password",
  templateUrl: "./set-password.component.html",
  styleUrl: "set-password.component.scss",
  imports: [ CapsLockNoticeComponent,
    HeadlineDescriptionComponent,
    HeadlineComponent,
    HeadlineTitleComponent,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    IconModule,
    NotifyChipComponent,
    NotifyChipValueComponent,
    TranslationPipeModule,
    TemplatePipeModule ],
  standalone: true,
  providers: [ provideTranslationScope(TRANSLATION_SCOPE) ]
})
export class SetPasswordComponent implements OnDestroy {
  private readonly _router: Router = inject(Router);

  private readonly _languageSelectionService: LanguageSelectionService = inject(LanguageSelectionService);

  protected readonly RESEND_LINK_COOLDOWN_SECONDS: typeof RESEND_LINK_COOLDOWN_SECONDS = RESEND_LINK_COOLDOWN_SECONDS;

  private _keyCloakService: KeycloakService = inject(KeycloakService);

  protected isAuthorized: Signal<boolean | undefined> = toSignal(this._keyCloakService.isAuthorized$());

  private isRouting: boolean = false;

  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  protected readonly _formBuilder: NonNullableFormBuilder = inject(NonNullableFormBuilder);

  private _authService: AuthService = inject(AuthService);

  protected readonly isLoading: WritableSignal<boolean> = signal(false);

  protected formState: WritableSignal<"normal" | "registration-expired" | "successful-changed" | "invalid-registration-link"> = signal("normal");


  public readonly formGroup: FormGroup<{
    newPassword: FormControl<string>;
    newPasswordRepeat: FormControl<string>;
    remember: FormControl<boolean>;
  }> = this._formBuilder.group({
      // eslint-disable-next-line @tseslint/unbound-method
      newPassword: this._formBuilder.control("", { validators: [ Validators.required, Validators.minLength(8), passwordValidator() ] }),
      // eslint-disable-next-line @tseslint/unbound-method
      newPasswordRepeat: this._formBuilder.control("", [ Validators.required, passwordMatchValidator("newPassword") ]),
      remember: this._formBuilder.control(false)
    });

  public get newPassword (): FormControl<string> {
    return this.formGroup.controls.newPassword;
  }

  public get newPasswordRepeat (): FormControl<string> {
    return this.formGroup.controls.newPasswordRepeat;
  }

  protected readonly hideNewPassword: WritableSignal<boolean> = signal(true);

  protected readonly hideNewPasswordRepeat: WritableSignal<boolean> = signal(true);

  protected readonly resendTriggered: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly canResend: WritableSignal<boolean> = signal(true);

  protected readonly cooldownSeconds: WritableSignal<number> = signal(0);

  protected readonly hasRequestedOnce: WritableSignal<boolean> = signal(false);

  protected readonly showResendCta: Signal<boolean> = computed(this._showResendCta.bind(this));

  private cooldownTimerId: ReturnType<typeof setInterval> | null = null;

  private readonly _registerKey: string | null;

  private storageUntilKey: string = 'setpwd:resendUntil:global';

  private storageRequestedKey: string = 'setpwd:hasRequestedOnce:global';

  public serverErrorMessage: WritableSignal<ServerErrorMessage> = signal({} as ServerErrorMessage);

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  public hasServerError: Signal<boolean> = computed(() => Boolean(this.serverErrorMessage().localizedMessage) || Boolean(this.serverErrorMessage().message));

  private _uuid: string = '';


  constructor () {
    this._faIconLibrary.addIcons(faEye, faEyeSlash, faExclamationCircle, faExclamationTriangle, faPaperPlane);

    const encodedKey: string = String(this._activatedRoute.snapshot.params[ 'registerKey' ]);

    if (encodedKey) {
      const decoded: string = atob(encodedKey);
      const parts: string[] = decoded.split(';');

      if (parts.length < 3 || !parts[ 0 ])
        throw new Error('Invalid registerKey');
      else
        this._uuid = parts[ 0 ];
    }

    this._languageSelectionService.setTemporarySubjectId(this._uuid);
    this.newPassword.valueChanges.subscribe(() => {
      this.newPasswordRepeat.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      this.serverErrorMessage.set({});
    });
    this._registerKey = this._activatedRoute.snapshot.paramMap.get(AUTHENTICATE_PARAM_MAPS.SET_PASSWORD_REGISTER_KEY);
    this.restoreCooldownFromStorage();
    this.register();
  }

  public ngOnDestroy (): void {
    // Clear subject-id when user leaves page
    this._languageSelectionService.setTemporarySubjectId(undefined);
  }

  private register (): void {
    if (!this._registerKey || this.isRouting) return;

    const isSubmitFlow: boolean = this.formGroup.valid && !this.hasServerError();
    const registerUser$: Observable<RegisterUserResponse> = isSubmitFlow
      ? this._authService.registerUser(this._registerKey, {
        newPassword: this.newPassword.value,
        newPasswordRepeat: this.newPasswordRepeat.value
      })
      : this._authService.registerUser(this._registerKey);

    registerUser$.subscribe((resp: RegisterUserResponse) => {
      if (resp.isRegistered) {
        this.isLoading.set(false);

        if (isSubmitFlow) {
          this.formState.set('successful-changed');
          this.clearResendState();
        }

        return;
      }

      if (resp.errorToken) {
        this.isLoading.set(false);

        switch (resp.errorToken) {
          case SET_PASSWORD_TOKENS.PASSWORD_POLICY_ERROR: {
            this.serverErrorMessage.set({
              localizedMessage: {
                token: 'status-password-used-before',
                defaultText: 'Passwort wurde zuvor schon einmal verwendet'
              }
            });

            break;
          }

          case REGISTER_USER_TOKENS.REGISTRATION_LINK_EXPIRED: {
            this.formState.set('registration-expired');

            break;
          }

          case REGISTER_USER_TOKENS.INVALID_REGISTRATION_LINK: {
            this.formState.set('invalid-registration-link');

            break;
          }

          case REGISTER_USER_TOKENS.REGISTRATION_ALREADY_DONE: {
            this.isRouting = true;
            void this._router.navigate(
              [ CORE_ROUTES.AUTHENTICATE, AUTHENTICATE_ROUTES.LOGIN ],
              {
                state: {
                  flash: { code: 'already-registered' }
                },
                replaceUrl: true
              }
            );

            break;
          }

          default: {
            break;
          }
        }

        return;
      }

      this.isLoading.set(false);
    });
  }

  protected handleToggleOfPassword (input: "new-password" | "new-password-repeat"): void {
    switch (input) {
      case "new-password": {
        this.hideNewPassword.set(!this.hideNewPassword());

        break;
      }

      case "new-password-repeat": {
        this.hideNewPasswordRepeat.set(!this.hideNewPasswordRepeat());

        break;
      }
    }
  }

  protected handleClickForget (): void {
    this.onBackToForgotPassword();
  }

  protected handleSubmit (): void {
    this.isLoading.set(true);
    this.register();
  }

  protected handleResendLink (): void {
    if (!this.canResend()) return;

    if (!this._uuid)
      return;

    this.isLoading.set(true);
    this._authService.sendPasswordResetMail(this._uuid, true, 'user-registration').subscribe({
      next: () => {
        this.hasRequestedOnce.set(true);

        try {
          localStorage.setItem(this.storageRequestedKey, '1');
        } catch {}

        this.resendTriggered.set(true);
        this.canResend.set(false);

        const until: number = Date.now() + RESEND_LINK_COOLDOWN_SECONDS * 1000;

        this.cooldownSeconds.set(RESEND_LINK_COOLDOWN_SECONDS);

        try {
          localStorage.setItem(this.storageUntilKey, String(until));
        } catch {}

        this.startCooldown(until);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  private startCooldown (untilTs: number): void {
    this.stopCooldown();
    this.cooldownTimerId = setInterval(() => {
      const left: number = Math.max(0, Math.ceil((untilTs - Date.now()) / 1000));

      this.cooldownSeconds.set(left);

      if (left <= 0) {
        this.stopCooldown();
        this.canResend.set(true);
        this.resendTriggered.set(false);

        try {
          localStorage.removeItem(this.storageUntilKey);
        } catch {}
      }
    }, 1000);
  }

  private restoreCooldownFromStorage (): void {
    try {
      const req: string | null = localStorage.getItem(this.storageRequestedKey);

      if (req === '1') this.hasRequestedOnce.set(true);
    } catch {}

    try {
      const raw: string | null = localStorage.getItem(this.storageUntilKey);

      if (!raw) return;

      const until: number = Number(raw);

      if (!Number.isFinite(until)) {
        localStorage.removeItem(this.storageUntilKey);

        return;
      }

      const left: number = Math.max(0, Math.ceil((until - Date.now()) / 1000));

      if (left > 0) {
        this.resendTriggered.set(true);
        this.canResend.set(false);
        this.cooldownSeconds.set(left);
        this.startCooldown(until);
      } else {
        localStorage.removeItem(this.storageUntilKey);
        this.canResend.set(true);
      }
    } catch {}
  }

  private stopCooldown (): void {
    if (this.cooldownTimerId) {
      clearInterval(this.cooldownTimerId);
      this.cooldownTimerId = null;
    }
  }

  protected onResendLink (e: Event): void {
    e.preventDefault();

    if (this.canResend()) this.handleResendLink();
  }

  private clearResendState (): void {
    try {
      localStorage.removeItem(this.storageUntilKey);
      localStorage.removeItem(this.storageRequestedKey);
    } catch {}

    this.resendTriggered.set(false);
    this.canResend.set(true);
    this.hasRequestedOnce.set(false);
    this.cooldownSeconds.set(0);
  }

  private _showResendCta (): boolean {
    return this.hasRequestedOnce() && this.canResend();
  }

  protected onBackToReferrerClick (): void {
    this.isRouting = true;

    if (this.isAuthorized())
      void this._router.navigateByUrl(CORE_ROUTES.PROJECTS);
    else
      this._authService.navigateToLogin();
  }

  protected onBackToForgotPassword (): void {
    this._authService.navigateToForgotPassword(``);
  }
}
